import type { Entitlement, MigrationLicenseType, PrincipalClass } from '../../api/types'
import { dateInputToIso } from '../../api/expiry'

// Onboarding wizard state and plan construction (ATC-133).
//
// PURE. The phase machine and the plan builder are separated from the executor
// (`runPlan.ts`) and from the page so that the parts carrying the failure
// semantics can be tested without mounting anything.

export type WizardPhase = 'collect' | 'review' | 'execute' | 'result'

export interface WizardInput {
  customerName: string
  entitledServices: Entitlement[]
  expiryDate: string
  consumableCredits: string
  poolCapacity: string
  issueFlightdeck: boolean
  flightdeckEntitlements: Entitlement[]
}

export const EMPTY_INPUT: WizardInput = {
  customerName: '',
  entitledServices: ['bootstrap', 'mint'],
  expiryDate: '',
  consumableCredits: '',
  poolCapacity: '',
  issueFlightdeck: false,
  flightdeckEntitlements: ['ppkg'],
}

export type StepKind = 'account' | 'grant' | 'environment' | 'map' | 'flightdeck'

export interface PlanStep {
  kind: StepKind
  /** Stable id, used as a React key and in the receipt. */
  id: string
  /** What the review screen shows, in the operator's words. */
  label: string
  /** True when a failure here leaves something an operator must go and look at. */
  irreversible: boolean
  licenseType?: MigrationLicenseType
  amount?: number
  entitledServices?: Entitlement[]
  expiresAt?: string
  principalClass?: PrincipalClass
}

export interface ValidationResult {
  ok: boolean
  errors: Partial<Record<keyof WizardInput, string>>
}

export function validate(input: WizardInput): ValidationResult {
  const errors: Partial<Record<keyof WizardInput, string>> = {}

  if (!input.customerName.trim()) {
    errors.customerName = 'A customer name is required.'
  }
  // ATC-140: environmentName is no longer collected. The label is
  // server-minted, and there is exactly one environment per customer, so the
  // wizard asks for ONE name — the customer's.
  if (input.entitledServices.length === 0) {
    // The server refuses it too; catching it here means the operator is not
    // told at step 3 of 5, after two writes have already landed.
    errors.entitledServices = 'An environment entitled to nothing cannot be issued.'
  }
  if (input.expiryDate && dateInputToIso(input.expiryDate) === null) {
    errors.expiryDate = 'Not a valid date.'
  }
  for (const [field, value] of [
    ['consumableCredits', input.consumableCredits],
    ['poolCapacity', input.poolCapacity],
  ] as const) {
    if (value !== '' && !/^\d+$/.test(value)) {
      errors[field] = 'Enter a whole number, or leave blank.'
    } else if (value !== '' && Number.parseInt(value, 10) < 1) {
      errors[field] = 'Must be at least 1.'
    }
  }
  if (input.issueFlightdeck && input.flightdeckEntitlements.length === 0) {
    errors.flightdeckEntitlements = 'A credential entitled to nothing cannot be issued.'
  }

  return { ok: Object.keys(errors).length === 0, errors }
}

/**
 * THE EXECUTION ORDER IS THE DESIGN, and it deliberately differs from the order
 * the operator filled the form in — which is why the review screen states it.
 *
 *   account -> grants -> environment -> map -> flightdeck
 *
 * The irreversible writes go LAST. A grant against an account with no
 * environments is harmless and, since `ATC-122`, correctable by an audited
 * adjustment. **Issuance is neither**: it mints a `customerEnvironmentId` and
 * reveals a raw token exactly once, so a failure after it leaves an orphan
 * environment and possibly a token nobody holds. Putting the cheap, correctable
 * writes first means a failure part-way through is as recoverable as it can be.
 */
export function buildPlan(input: WizardInput): PlanStep[] {
  const steps: PlanStep[] = [
    {
      kind: 'account',
      id: 'account',
      label: `Create customer “${input.customerName.trim()}”`,
      irreversible: false,
    },
  ]

  const consumable = Number.parseInt(input.consumableCredits, 10)
  if (Number.isInteger(consumable) && consumable > 0) {
    steps.push({
      kind: 'grant',
      id: 'grant-consumable',
      label: `Grant ${consumable} consumable credit(s)`,
      irreversible: false,
      licenseType: 'consumable',
      amount: consumable,
    })
  }

  const pool = Number.parseInt(input.poolCapacity, 10)
  if (Number.isInteger(pool) && pool > 0) {
    steps.push({
      kind: 'grant',
      id: 'grant-pool',
      label: `Grant ${pool} pool capacity`,
      irreversible: false,
      licenseType: 'pool',
      amount: pool,
    })
  }

  // ATC-139. THE LABEL STATES THE TERM. The review screen's entire job is to
  // enumerate the writes before anything happens, and it never mentioned the
  // expiry — so an operator who set one had no confirmation, and one who did
  // not had no warning. Hotfix 054 was invisible partly for this reason.
  const term = input.expiryDate ? `expires ${input.expiryDate}` : 'perpetual'

  steps.push({
    kind: 'environment',
    id: 'environment',
    label: `Issue the environment (${term})`,
    // Mints a GUID and reveals a token exactly once. Not undoable: the only
    // "undo" is `status: 'revoked'`, which writes a revocation that never had a
    // business meaning.
    irreversible: true,
    entitledServices: input.entitledServices,
    expiresAt: input.expiryDate ? (dateInputToIso(input.expiryDate) ?? undefined) : undefined,
    principalClass: 'device',
  })

  steps.push({
    kind: 'map',
    id: 'map',
    label: 'Map the environment to the customer',
    irreversible: false,
  })

  if (input.issueFlightdeck) {
    steps.push({
      kind: 'flightdeck',
      id: 'flightdeck',
      label: `Issue the Flight Deck credential (${term})`,
      irreversible: true,
      entitledServices: input.flightdeckEntitlements,
      // ATC-139. THE SAME TERM AS THE DEVICE CREDENTIAL, and this closes a gap
      // the wizard was creating. `ATC-ADR-011`'s R-ATC-10 amendment made the
      // PATCH expiry action apply to EVERY row in the partition precisely
      // because "leaving the `atc_fd_` credential alive past the customer's
      // term is precisely the failure the status path's all-rows default
      // exists to prevent". Issuance did not follow that rule: the wizard
      // always minted a PERPETUAL Flight Deck credential beside a
      // time-boxed device one, creating exactly the state the ADR forbids.
      expiresAt: input.expiryDate ? (dateInputToIso(input.expiryDate) ?? undefined) : undefined,
      principalClass: 'flightdeck',
    })
  }

  return steps
}
